import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, FileText, Home, Phone, Mail } from 'lucide-react';
import { Logo, FooterLogo } from '../components/LogoOptions';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Chatbot from '../components/Chatbot';

const HomePage = () => {
  const navigate = useNavigate();

  const handleServiceSelect = (serviceType) => {
    navigate('/intake', { 
      state: { serviceType: serviceType },
      replace: false 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-6 text-sm">
            {/* Mobile: Icon only */}
            <a href="tel:206-627-0768" className="sm:hidden flex items-center justify-center w-10 h-10 bg-amber-500 hover:bg-amber-600 text-white rounded-full transition-colors shadow-md">
              <Phone className="w-5 h-5" />
            </a>
            {/* Desktop: Full contact info */}
            <a href="tel:206-627-0768" className="hidden sm:flex items-center gap-2 text-gray-700 hover:text-amber-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">206-627-0768</span>
            </a>
            <a href="mailto:info@ezflowtc.com" className="hidden md:flex items-center gap-2 text-gray-700 hover:text-amber-600 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="font-medium">info@ezflowtc.com</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <CheckCircle2 className="w-4 h-4" />
            Professional Transaction Coordination
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Seamless Real Estate
            <span className="block text-amber-600 mt-2">Transaction Management</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Focus on closing deals while we handle the paperwork. Transaction coordination from contract to close for just $300.
          </p>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-8 mb-12 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
              <span>Fast 24-Hour Response</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
              <span>Licensed Professionals</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
              <span>Flat Rate Pricing</span>
            </div>
          </div>

          {/* Service Area Disclaimer */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Service Area:</span> We proudly serve transactions within Washington State and the Northwest Multiple Listing Service (NWMLS).
              </p>
            </div>
          </div>
        </div>

        {/* Service Selection */}
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-4">
            Select a Service to Get Started
          </h2>
          <p className="text-center text-gray-600 mb-10">Choose the service that matches your transaction needs</p>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Service Card 1 */}
            <Card 
              className="group cursor-pointer border-2 border-gray-200 hover:border-amber-500 hover:shadow-2xl transition-all duration-300 overflow-hidden bg-white"
              onClick={() => handleServiceSelect('Listing / Buying Side (In Contract)')}
            >
              <div className="p-8 lg:p-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-500 transition-colors duration-300">
                    <FileText className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors duration-300" strokeWidth={2} />
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">$300</div>
                    <div className="text-sm text-gray-500">Flat Rate</div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Listing / Buying Side
                </h3>
                <p className="text-sm font-semibold text-amber-600 mb-4">(In Contract)</p>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Complete transaction coordination from mutual acceptance to closing. We manage deadlines, communications, and documentation.
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Deadline tracking & reminders</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Title & escrow coordination</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Document management</span>
                  </li>
                </ul>

                <Button className="w-full bg-gray-900 hover:bg-amber-600 text-white py-6 text-lg font-semibold transition-colors duration-300">
                  Select This Service
                </Button>
              </div>
            </Card>

            {/* Service Card 2 */}
            <Card 
              className="group cursor-pointer border-2 border-gray-200 hover:border-amber-500 hover:shadow-2xl transition-all duration-300 overflow-hidden bg-white"
              onClick={() => handleServiceSelect('Listing Side Prep')}
            >
              <div className="p-8 lg:p-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-500 transition-colors duration-300">
                    <Home className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors duration-300" strokeWidth={2} />
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">$300</div>
                    <div className="text-sm text-gray-500">Flat Rate</div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Listing Side Prep
                </h3>
                <p className="text-sm font-semibold text-amber-600 mb-4">(Pre-Contract)</p>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Get your listing ready for the market. We prepare all necessary documentation and ensure compliance before going live.
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Listing documentation prep</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Compliance verification</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span>Pre-market checklist</span>
                  </li>
                </ul>

                <Button className="w-full bg-gray-900 hover:bg-amber-600 text-white py-6 text-lg font-semibold transition-colors duration-300">
                  Select This Service
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6">
              <div className="flex justify-center mb-4">
                <FooterLogo />
              </div>
              <p className="text-gray-400 max-w-md mx-auto">
                Professional transaction coordination services for real estate agents and brokers.
              </p>
            </div>
            
            {/* Client Portal Link */}
            <div className="mb-8">
              <button
                onClick={() => navigate('/portal')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl"
                data-testid="portal-link-btn"
              >
                <FileText className="w-5 h-5" />
                Client Portal - Track Your Transactions
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8 pb-8 border-b border-gray-700">
              <a href="tel:206-627-0768" className="flex items-center gap-2 text-gray-300 hover:text-amber-500 transition-colors">
                <Phone className="w-5 h-5" />
                <span className="font-medium">206-627-0768</span>
              </a>
              <a href="mailto:info@ezflowtc.com" className="flex items-center gap-2 text-gray-300 hover:text-amber-500 transition-colors">
                <Mail className="w-5 h-5" />
                <span className="font-medium">info@ezflowtc.com</span>
              </a>
            </div>
            
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} EZ Flow TC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* AI Chatbot */}
      <Chatbot />
    </div>
  );
};

export default HomePage;
